import { FiBook } from 'react-icons/fi';

export const awlSublist4Data = {
  courseId: 'awl-sublist-4',
  courseType: 'vocabulary',
  title: 'Academic Word List: Sublist 4',
  icon: FiBook,
  overview: {
    impact: 'Handle research and policy vocabulary so Reading paraphrases and Task 2 arguments sound academic',
    focus: 'investigate, hypothesis, implementation — plus access, adequate, predict, subsequent',
    uses: 'Reading passages on research and government; Writing Task 2 on education, health, and planning',
    duration: '20-25 minutes',
  },
  sections: [
    {
      title: 'Word meanings and usage',
      content: `<p>Coxhead’s Academic Word List Sublist 4 sits just below the ultra-high-frequency words of Sublists 1–3. These headwords still appear across research papers, policy reports, and IELTS Reading passages on science, education, and government. If you recognise them quickly, matching-heading and Yes/No/Not Given items become faster because the paraphrase is often an AWL partner rather than a rare synonym.</p>
<p>The full Sublist 4 headword set in this course is: access, adequate, annual, apparent, approximate, attitude, attribute, commit, communicate, concentrate, confer, contrast, cycle, debate, despite, dimension, emerge, error, goal, grant, hence, hypothesis, implementation, impose, integrate, investigate, mechanism, obvious, option, overall, parallel, predict, prior, professional, promote, resolve, retain, series, statistic, subsequent, summary, undertake.</p>
<p>You do not need every form in one essay. Learn a core of twelve words deeply — meaning, word family, and two collocations — then recycle them in Task 2 topic sentences. Words such as <strong>investigate</strong>, <strong>hypothesis</strong>, and <strong>implementation</strong> are especially useful when you discuss research, policy, or how a proposal would work in practice.</p>`,
      points: [
        'These words are common in policy, economics, and social-science texts.',
        'IELTS Reading often paraphrases “look into” as investigate and “guess” as hypothesis or predict.',
        'Using them correctly in essays signals academic fluency without sounding rare or forced.',
      ],
      table: {
        headers: ['Word', 'Definition', 'Example Sentence'],
        rows: [
          ['access', 'The right or opportunity to use or approach something', 'All students should have equal access to quality education.'],
          ['adequate', 'Sufficient or satisfactory for a particular purpose', 'The funding was adequate for a pilot scheme but not for a national rollout.'],
          ['annual', 'Occurring once every year', 'The annual report showed a steady increase in revenue.'],
          ['apparent', 'Clearly visible or understood; seeming but not necessarily true', 'It became apparent that the policy had failed to achieve its goals.'],
          ['approximate', 'Close to an exact amount but not completely accurate', 'The approximate cost of the project is two million dollars.'],
          ['attitude', 'A settled way of thinking or feeling about something', 'Public attitudes towards climate change have shifted significantly.'],
          ['attribute', 'A quality or feature; to regard as caused by', 'Patience is a key attribute of effective teachers.'],
          ['hypothesis', 'A proposed explanation that can be tested', 'The researchers tested the hypothesis that later start times improve concentration.'],
          ['implementation', 'The process of putting a plan or decision into effect', 'Poor implementation, not the idea itself, weakened the reform.'],
          ['investigate', 'To examine a problem or situation systematically', 'The team will investigate the unexpected decline in scores.'],
          ['predict', 'To say what will happen based on evidence', 'Economists predict a slower cycle of growth next year.'],
          ['subsequent', 'Coming after something in time', 'Subsequent studies failed to confirm the original claim.'],
        ],
      },
    },
    {
      title: 'Word families',
      content: `<p>Knowing different forms of a word lets you paraphrase in Reading and vary grammar in Writing. Examiners notice when a candidate can move from <em>investigate</em> to <em>investigation</em> or from <em>implement</em> to <em>implementation</em> without repeating the same slot. Incorrect forms — “an investigate,” “very implementation” — are easy Grammar-score leaks.</p>
<p>Several Sublist 4 items work as both noun and verb (<em>access, contrast, grant, debate</em>). Check the sentence slot before you write. After an article you almost always need a noun: “an investigation,” not “an investigate.” After a modal you need a base verb: “governments should investigate,” not “should investigation.”</p>`,
      points: [
        'Nouns, verbs, and adjectives often share the same root — learn the set, not one form.',
        'Implementation and investigation are high-value nouns for Task 2 topic sentences.',
        'Hence is a linker, not a content noun; do not treat it like a synonym of “because.”',
      ],
      table: {
        headers: ['Base Word', 'Noun', 'Verb', 'Adjective/Adverb'],
        rows: [
          ['access', 'access, accessibility', 'access', 'accessible'],
          ['adequate', 'adequacy', '—', 'adequate, adequately, inadequate'],
          ['annual', '—', '—', 'annual, annually'],
          ['apparent', '—', '—', 'apparent, apparently'],
          ['approximate', 'approximation', 'approximate', 'approximate, approximately'],
          ['attitude', 'attitude', '—', 'attitudinal'],
          ['attribute', 'attribute, attribution', 'attribute', 'attributable'],
          ['hypothesis', 'hypothesis', 'hypothesise', 'hypothetical, hypothetically'],
          ['implement', 'implementation', 'implement', 'implemented'],
          ['investigate', 'investigation, investigator', 'investigate', 'investigative'],
          ['predict', 'prediction', 'predict', 'predictable, unpredictable'],
          ['subsequent', '—', '—', 'subsequent, subsequently'],
        ],
      },
    },
    {
      title: 'Common collocations',
      content: `<p>Academic words are best learned as partnerships. “Carry out an investigation” and “test a hypothesis” sound fluent; “do an investigate” does not. Collocations also speed Reading: if the passage says “limited access to,” the question may say “few people can use.”</p>
<p>Memorise two or three partnerships per target word. For policy essays, <em>implement a policy</em>, <em>impose a tax</em>, <em>promote equality</em>, and <em>resolve a dispute</em> cover a surprising number of Task 2 prompts. For science-style Reading, watch <em>test a hypothesis</em>, <em>statistical error</em>, and <em>subsequent studies</em>.</p>`,
      points: [
        'Collocations raise Lexical Resource more than isolated “smart” words.',
        'Reading often paraphrases a collocation rather than a single synonym.',
      ],
      examples: [
        { label: 'access', text: 'gain access to, have access to, equal access, limited access, access to information' },
        { label: 'adequate', text: 'adequate provision, adequate funding, adequate resources, perfectly adequate' },
        { label: 'attitude', text: 'public attitude towards, change in attitude, adopt an attitude, positive attitude' },
        { label: 'hypothesis', text: 'test a hypothesis, support a hypothesis, working hypothesis' },
        { label: 'implementation', text: 'policy implementation, delay implementation, effective implementation' },
        { label: 'investigate', text: 'investigate a decline, investigate the cause, further investigation' },
        { label: 'predict', text: 'predict a trend, hard to predict, predict the outcome' },
        { label: 'subsequent', text: 'subsequent research, subsequent years, subsequent studies' },
        { label: 'impose', text: 'impose a ban, impose a tax, impose restrictions' },
        { label: 'undertake', text: 'undertake a study, undertake a review, undertake reform' },
      ],
    },
    {
      title: 'Using Sublist 4 in IELTS writing',
      content: `<p>Sublist 4 words are especially useful for describing research processes and evaluating proposals. Replace informal “look into” with <em>investigate</em>, “enough” with <em>adequate</em>, “about” with <em>approximately</em>, and “after that” with <em>subsequent</em>. When you discuss whether a policy will work, name the <em>implementation</em>, not just the idea.</p>
<p>A clean Task 2 move is cause then investigation: “Governments should investigate why access remains unequal before they impose new targets.” That sentence uses three list words without listing them. Do not force <em>hence</em> into every conclusion; <em>therefore</em> is often clearer.</p>`,
      points: [
        'Use investigate / hypothesis / predict for research-style arguments.',
        'Use access, adequate, implement, and impose for policy arguments.',
        'Despite is a preposition: “despite the cost,” not “despite of the cost.”',
      ],
      examples: [
        { label: 'Weak sentence', text: 'They should look into why scores went down and then do the plan.' },
        { label: 'Strong sentence', text: 'The team should investigate the decline in scores before the full implementation of the plan.' },
        { label: 'Weak sentence', text: 'People cannot easily get good healthcare in villages.' },
        { label: 'Strong sentence', text: 'Access to adequate healthcare remains limited in rural communities.' },
      ],
    },
  ],
  exercises: [
    {
      title: 'Match word to definition',
      type: 'select',
      instructions: 'Choose the correct definition for each academic word.',
      questions: [
        {
          text: 'What does “adequate” mean?',
          options: ['More than enough', 'Sufficient for a particular purpose', 'Completely lacking'],
          answer: 'Sufficient for a particular purpose',
          explanation: '“Adequate” means just enough — satisfactory but not exceptional. It is weaker than “abundant.”',
        },
        {
          text: 'What does “hypothesis” mean?',
          options: ['A proven law of nature', 'A proposed explanation that can be tested', 'A random guess with no purpose'],
          answer: 'A proposed explanation that can be tested',
          explanation: 'A hypothesis is a testable claim, not a final proof. Scientists investigate whether evidence supports it.',
        },
        {
          text: 'What does “subsequent” mean?',
          options: ['Coming after something in time', 'Happening at the same time', 'Happening before something'],
          answer: 'Coming after something in time',
          explanation: '“Subsequent studies” are later studies. The opposite idea is prior, which is also on this sublist.',
        },
        {
          text: 'What does “implementation” mean?',
          options: ['The process of putting a plan into effect', 'The first idea before any plan exists', 'A type of graph in Task 1'],
          answer: 'The process of putting a plan into effect',
          explanation: 'Implementation is the doing stage. Essays often argue that a policy failed because implementation was weak.',
        },
        {
          text: 'What does “apparent” mean?',
          options: ['Hidden from view', 'Clearly visible or understood', 'Officially illegal'],
          answer: 'Clearly visible or understood',
          explanation: '“Apparent” can mean obvious, or only seeming to be true. Context tells you which reading is intended.',
        },
      ],
    },
    {
      title: 'Complete the sentence',
      type: 'fill-in',
      instructions: 'Fill in the blank with the correct Sublist 4 word.',
      questions: [
        {
          text: 'The team will ____ the unexpected decline in scores.',
          answer: 'investigate',
          explanation: 'After “will” you need the base verb. Investigate means examine systematically — the exact job of a research team.',
        },
        {
          text: 'All students should have equal ____ to laboratory equipment.',
          answer: 'access',
          explanation: '“Have access to” is the standard collocation for the right or opportunity to use something.',
        },
        {
          text: 'The ____ cost of the renovation is around $500,000.',
          answer: 'approximate',
          explanation: '“Approximate” means close to but not exact, which matches an estimate introduced by “around.”',
        },
        {
          text: 'Researchers ignored the first result, but ____ studies failed to confirm it.',
          answer: 'subsequent',
          explanation: 'Subsequent means later in time. It is the academic alternative to informal “later” or “after that.”',
        },
      ],
    },
    {
      title: 'Choose the correct word form',
      type: 'select',
      instructions: 'Select the grammatically correct form for each sentence.',
      questions: [
        {
          text: 'The team will ____ the unexpected decline in scores. (investigate)',
          options: ['investigate', 'investigation', 'investigative'],
          answer: 'investigate',
          explanation: 'A modal-like future “will” needs a base verb. Investigation is a noun; investigative is an adjective.',
        },
        {
          text: 'Poor ____, not the idea itself, weakened the reform. (implement)',
          options: ['implement', 'implementation', 'implementing'],
          answer: 'implementation',
          explanation: 'After the adjective “poor” you need a noun. Implementation names the process of putting the plan into effect.',
        },
        {
          text: 'The results were only ____ — they were not exact. (approximate)',
          options: ['approximately', 'approximate', 'approximation'],
          answer: 'approximate',
          explanation: 'After “were” you need an adjective describing the results. Approximately is an adverb; approximation is a noun.',
        },
        {
          text: 'It became ____ that the targets were unrealistic. (apparent)',
          options: ['apparent', 'apparently', 'appear'],
          answer: 'apparent',
          explanation: '“Become apparent” is a fixed pairing that needs the adjective. Apparently would modify a whole clause instead.',
        },
      ],
    },
  ],
  tips: [
    'Learn investigate / investigation / investigative as a set so you never drop the wrong form after “will” or “a.”',
    'Pair hypothesis with “test” or “support,” not with informal “guess.”',
    'Use access and adequate together for education and health essays: “access to adequate…”',
    'Despite takes a noun phrase: “despite limited funding,” not “despite of.”',
  ],
  dailyPractice: [
    'Write one Task 2 sentence that uses investigate and implementation together.',
    'Read a science news paragraph and highlight any Sublist 4 words from the full list above.',
    'Transform four informal phrases: look into → investigate, enough → adequate, about → approximately, after that → subsequent.',
    'Quiz yourself on the twelve table words: cover the definition column and say each meaning aloud.',
  ],
  backPath: '/exams/english/#vocabulary',
  backLabel: 'Back to Vocabulary',
  aiContext: 'AWL Sublist 4 vocabulary course covering Coxhead headwords such as access, adequate, annual, apparent, approximate, attitude, attribute, hypothesis, implementation, investigate, predict, and subsequent. Includes the full stub word list, word families, collocations, and IELTS Writing integration for research and policy topics.',
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...awlSublist4Data,
      title: awlSublist4Data.title + ' (Foundations)',
      overview: { ...awlSublist4Data.overview, duration: '15-20 minutes' },
      sections: awlSublist4Data.sections.slice(0, Math.max(2, Math.ceil(awlSublist4Data.sections.length / 2))),
      exercises: awlSublist4Data.exercises.slice(0, 1).map((ex) => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...awlSublist4Data,
      title: awlSublist4Data.title + ' (Advanced)',
      overview: { ...awlSublist4Data.overview, duration: '30-35 minutes' },
      sections: awlSublist4Data.sections,
      exercises: awlSublist4Data.exercises.map((ex) => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(awlSublist4Data.tips || []),
        'For Band 8+: use implementation to evaluate a policy, not only to name it.',
        'Advanced tip: contrast prior and subsequent evidence in one sentence.',
      ],
    };
  }
  return awlSublist4Data;
}
